import React, { useContext, useEffect } from "react";
import { Box, Typography, Button } from "@mui/material";
import ResponsiveDialog from "./ResponsiveDialog.js";
import { AuthContext } from "src/utils/AuthContext.js";
import { customEvent } from "src/utils/gtag.js";
import {
  getCalendarApiUrl,
  getWebcalFormat,
  handleOpenWebcal,
} from "src/utils/Utils.js";
import { useNavigate } from "react-router-dom";
import { getPageRoute } from "src/services/Config.js";
import { finalPulse } from "../ProgressButton.js";
import { useGetCalendar } from "src/services/queries/useGetCalendar.js";

const OpenCalendarDialogContent = ({
  handleOpenCalendar,
  calendar,
  loadFailed,
  onRetry,
}) => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        textAlign: "left",
        padding: 2,
      }}
    >
      {/* Pagination Dots at the top */}
      <Box sx={{ display: "flex", justifyContent: "center", gap: 1, mb: 4 }}>
        <Box
          sx={{
            width: "24px",
            height: "8px",
            borderRadius: "4px",
            backgroundColor: "primary.main",
          }}
        />
        <Box
          sx={{
            width: "8px",
            height: "8px",
            borderRadius: "4px",
            backgroundColor: "action.disabled",
          }}
        />
      </Box>

      {/* Current step header with number */}
      <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
        <Box
          sx={{
            width: "32px",
            height: "32px",
            borderRadius: "50%",
            backgroundColor: "primary.main",
            color: "white",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "16px",
            fontWeight: 600,
            mr: 2,
          }}
        >
          1
        </Box>
        <Typography
          variant="title"
          sx={{ fontSize: "26px", textWrap: "pretty" }}
        >
          Open Your Calendar
        </Typography>
      </Box>

      <Typography variant="body1" sx={{ mb: 3 }}>
        Follow the instructions in your calendar app to finish subscribing.
      </Typography>
      <Typography variant="body1" sx={{ mb: 4 }}>
        Once you're done, pop back here to confirm so your subscription stays
        active – and if it doesn't go through, we'll help you fix it.
      </Typography>
      <Button
        variant="contained"
        onClick={loadFailed ? onRetry : handleOpenCalendar}
        disabled={!calendar?.stanzaLink && !loadFailed}
        data-testid="open-google-calendar"
        sx={{
          width: "100%",
          animation: `${finalPulse} 3s infinite`,
          mb: user?.provider === "google" ? 2 : 4,
        }}
      >
        <span>{loadFailed ? "TRY AGAIN" : "OPEN CALENDAR"}</span>
      </Button>

      {/* Google Connect option - only shown for Google users */}
      {user?.provider === "google" && (
        <>
          <Button
            variant="outlined"
            onClick={() => {
              customEvent({
                name: "user_click_switch_google_connect",
                category: "SubscribingCalendar",
                label: "Switch to Google Connect Click",
                data: {
                  user_id: user?._id,
                  user_email: user?.email,
                  user_handle: user?.handle ?? "no_handle",
                  calendar_id: calendar?._id,
                },
              });
              navigate(
                getPageRoute("help", "SUBSCRIBING_GOOGLE_CALENDAR_STEPS", {
                  handle: calendar?.handle,
                  calendarId: calendar?._id ?? calendar?.id,
                }),
                { state: { calendar } }
              );
            }}
            sx={{
              width: "100%",
              display: "flex",
              alignItems: "center",
              gap: 1,
              mb: 2,
            }}
          >
            SWITCH TO GOOGLE CONNECT
          </Button>
          <Typography
            variant="body2"
            sx={{ mb: 4, color: "text.secondary", textAlign: "left" }}
          >
            <strong>Recommended for Google users:</strong> We use Google
            Calendar's API to automatically sync across all your devices,
            providing faster updates and a seamless experience. No manual setup
            required.
          </Typography>
        </>
      )}

      {/* Show upcoming steps below current step content */}
      <Box sx={{ pt: 3, borderTop: "1px solid", borderColor: "divider" }}>
        <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
          What's next:
        </Typography>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            mb: 1.5,
            opacity: 0.6,
          }}
        >
          <Box
            sx={{
              width: "20px",
              height: "20px",
              borderRadius: "50%",
              backgroundColor: "action.disabled",
              color: "white",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "12px",
              fontWeight: 600,
              mr: 2,
            }}
          >
            2
          </Box>
          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
              fontSize: "14px",
            }}
          >
            Verify and Troubleshoot
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

const OpenCalendarDialog = ({ open, handleClose, calendar: calendarProp }) => {
  const { user } = useContext(AuthContext);
  // Callers may pass a trimmed calendar; resolve the full one for its stanzaLink.
  const {
    data: fetchedCalendar,
    isFetching: isLoadingCalendar,
    isError: isCalendarError,
    refetch: refetchCalendar,
  } = useGetCalendar(
    { handle: calendarProp?.handle, calendarId: calendarProp?._id },
    {
      enabled:
        open &&
        !calendarProp?.stanzaLink &&
        !!calendarProp?.handle &&
        !!calendarProp?._id,
    }
  );
  // keepPreviousData can hold the previous calendar's result while this one loads.
  const matchesProp =
    !!fetchedCalendar &&
    String(fetchedCalendar._id) === String(calendarProp?._id);
  const calendar =
    !calendarProp?.stanzaLink && matchesProp && fetchedCalendar.stanzaLink
      ? fetchedCalendar
      : calendarProp;
  const loadFailed =
    !calendar?.stanzaLink && isCalendarError && !isLoadingCalendar;
  const calendarApiUrl = getCalendarApiUrl({ calendar, user });
  const navigate = useNavigate();

  useEffect(() => {
    if (open) {
      customEvent({
        name: "landed_on_open_calendar_dialog",
        category: "SubscribingCalendar",
        label: "Landed on Open Calendar Dialog",
        data: {
          user_id: user?._id,
          user_email: user?.email,
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleOpenCalendar = () => {
    customEvent({
      name: `user_click_open_calendar`,
      category: "SubscribingCalendar",
      label: "Open Calendar Click",
      data: {
        user_id: user._id,
        user_email: user.email,
        user_handle: user.handle ?? "no_handle",
      },
    });

    customEvent({
      name: `click_open_calendar_${user.provider}`,
      category: "SubscribingCalendar",
      label: "Open Calendar Click",
      data: {
        user_id: user._id,
        user_email: user.email,
        user_handle: user.handle ?? "no_handle",
      },
    });

    handleOpenWebcal(user, getWebcalFormat(calendarApiUrl), calendar);

    navigate(
      getPageRoute(
        "help",
        "SUBSCRIBING_CALENDAR_VERIFY",
        { handle: calendar.handle, calendarId: calendar._id },
        { email: user.email, is_reminder: false }
      ),
      {
        state: { calendar },
      }
    );
  };

  return (
    <ResponsiveDialog
      open={open}
      handleClose={handleClose}
      hideCloseButton
      bottomProps={{ hideRectangle: true, bubbleStyle: true }}
      centerProps={{
        sx: { height: "auto", ".MuiPaper-root": { maxWidth: "393px" } },
      }}
    >
      <OpenCalendarDialogContent
        handleOpenCalendar={handleOpenCalendar}
        calendar={calendar}
        loadFailed={loadFailed}
        onRetry={refetchCalendar}
      />
    </ResponsiveDialog>
  );
};

export default OpenCalendarDialog;
